import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead, Rise } from '../ui/screen-parts.js';
import { Chip, Empty, Modal, Panel, Segments, useFlash, useModal } from '../ui/kit.js';
import { Done, Note, Slots, hashed, iso, money, useLast } from '../ui/actions/parts.js';
import { printDate, nextId } from '../state/building.js';

import SwedishMassage from "../media/images/spa-screen/swedish-massage.jpeg";
import DeepTissueMassage from "../media/images/spa-screen/deep-tissue-massage.jpg";
import Facial from "../media/images/spa-screen/facial-massage.jpg";
import Manicure from "../media/images/spa-screen/manicure.jpg";
import Pedicure from "../media/images/spa-screen/pedicure.jpg";
import HotStoneMassage from "../media/images/spa-screen/hot-stone-massage.jpg";

const EASE = [0.16, 1, 0.3, 1];

const services = [
    { name: "Swedish Massage", description: "A relaxing full-body massage", price: "$50", minutes: 60, imageUrl: SwedishMassage },
    { name: "Deep Tissue Massage", description: "A therapeutic massage focusing on deep layers", price: "$70", minutes: 60, imageUrl: DeepTissueMassage },
    { name: "Facial", description: "Cleansing and exfoliating facial treatment", price: "$60", minutes: 50, imageUrl: Facial },
    { name: "Manicure", description: "Classic manicure with nail polish", price: "$30", minutes: 40, imageUrl: Manicure },
    { name: "Pedicure", description: "Foot spa treatment with polish", price: "$35", minutes: 45, imageUrl: Pedicure },
    { name: "Hot Stone Massage", description: "A massage using heated stones", price: "$80", minutes: 75, imageUrl: HotStoneMassage }
];

const cost = service => Number(service.price.replace(/[^0-9.]/g, ''));

/* The spa books from 9 AM, with the last treatment starting at 6 PM. */
const TIMES = ['9:00 AM', '10:30 AM', '12:00 PM', '1:30 PM', '3:00 PM', '4:30 PM', '6:00 PM'];
const HOURS = [9, 10.5, 12, 13.5, 15, 16.5, 18];
const THERAPISTS = ['No preference', 'Female therapist', 'Male therapist'];

/* Other residents' bookings: the same slots read taken every visit. */
const takenByOthers = (date, time) => hashed(`${date}${time}`, 3) === 0;

function SpaScreen()
{
    const [flash, notify] = useFlash();
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [availabilityMessage, setAvailabilityMessage] = useState(null);
    const [checked, setChecked] = useState(null);     // the date whose slots are showing
    const [slot, setSlot] = useState('');
    const [chosen, setChosen] = useState(null);       // the selected treatment's name
    const [bookings, setBookings] = useState([]);
    const sheet = useModal();
    const reduced = useReducedMotion();

    const slotsFor = date => TIMES.map(time => ({
        time,
        taken: takenByOthers(date, time) || bookings.some(b => b.date === date && b.time === time)
    }));

    const checkAvailability = () =>
    {
        if (!selectedDate) {
            setChecked(null);
            return setAvailabilityMessage('Please select a date first. The time is optional.');
        }

        const slots = slotsFor(selectedDate);
        const open = slots.filter(s => !s.taken);
        setChecked(selectedDate);

        if (open.length === 0) {
            setSlot('');
            return setAvailabilityMessage(`The spa is fully booked on ${printDate(selectedDate)}. Try the next day.`);
        }

        /* With a time asked for, offer the open slot nearest to it. */
        if (selectedTime) {
            const [h, m] = selectedTime.split(':').map(Number);
            const wanted = h + m / 60;
            const nearest = open.reduce((best, s) => (
                Math.abs(HOURS[TIMES.indexOf(s.time)] - wanted) < Math.abs(HOURS[TIMES.indexOf(best.time)] - wanted) ? s : best
            ));
            const inHours = wanted >= 9 && wanted <= 18;
            setSlot(nearest.time);
            setAvailabilityMessage(inHours
                ? `${open.length} openings on ${printDate(selectedDate)}. ${nearest.time} is the closest to the time you asked for.`
                : `Sorry, no appointments at that hour; the spa books from 9 AM to 6 PM. ${nearest.time} is the closest opening.`);
        } else {
            setSlot('');
            setAvailabilityMessage(`${open.length} openings on ${printDate(selectedDate)}. Pick a time, then a treatment.`);
        }
    };

    const book = details => {
        setBookings(list => [...list, { id: nextId(), ...details }]);
        setSlot('');
    };

    const cancel = booking => {
        setBookings(list => list.filter(b => b.id !== booking.id));
        notify(`${booking.service} on ${printDate(booking.date)} at ${booking.time} cancelled. No charge.`);
    };

    const picked = services.find(s => s.name === chosen);

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Spa" title="Book a treatment" />
            {flash}

            <Rise className="schedule-card">
                <h3>Schedule your appointment</h3>
                <div className="spa-picker">
                    <div>
                        <label htmlFor="date-picker" className="form-label">Select date</label>
                        <input type="date" id="date-picker" value={selectedDate} min={iso(0)}
                            onChange={e => { setSelectedDate(e.target.value); setChecked(null); setAvailabilityMessage(null); }}
                            className="form-control" />
                    </div>
                    <div>
                        <label htmlFor="time-picker" className="form-label">Select time</label>
                        <input type="time" id="time-picker" value={selectedTime}
                            onChange={e => setSelectedTime(e.target.value)} className="form-control" />
                    </div>
                </div>

                <button type="button" className="btn btn-primary" onClick={checkAvailability}>
                    Check availability
                </button>

                {availabilityMessage && (
                    <p className="availability-message" role="status">{availabilityMessage}</p>
                )}

                <AnimatePresence initial={false}>
                    {checked && (
                        <motion.div
                            className="spa-slots"
                            initial={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                        >
                            <div className="spa-slots__inner">
                                <span className="form-label">Open times, {printDate(checked)}</span>
                                <Slots slots={slotsFor(checked)} value={slot} onChange={setSlot} label="Appointment time" />

                                <div className="spa-summary">
                                    <span className="row__meta">
                                        {picked ? `${picked.name}, ${picked.minutes} min, ${picked.price}` : 'No treatment chosen yet. Tap one below.'}
                                        {slot ? ` · ${slot}` : ''}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        disabled={!picked}
                                        onClick={() => sheet.open({ service: picked })}
                                    >
                                        {picked ? `Book ${picked.name}${slot ? ` at ${slot}` : ''}` : 'Choose a treatment'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Rise>

            {bookings.length > 0 && (
                <Panel className="spa-booked">
                    <h4>Your appointments</h4>
                    <ul className="rows">
                        {bookings.map(b => (
                            <li className="row" key={b.id}>
                                <div className="row__main">
                                    <span className="row__title">{b.service}</span>
                                    <span className="row__meta">{printDate(b.date)} at {b.time} · {b.minutes} min · {b.therapist === THERAPISTS[0] ? 'any therapist' : b.therapist.toLowerCase()}</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone="ok">Booked</Chip>
                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => cancel(b)}>Cancel</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Panel>
            )}

            <h3 style={{ marginTop: 'clamp(2rem, 4vw, 3rem)' }}>Spa services</h3>

            <div className="spa-grid">
                {services.map((service, index) => {
                    const on = chosen === service.name;
                    return (
                        <Rise className={`services-card spa-card${on ? ' spa-card--on' : ''}`} key={service.name} index={index}>
                            <button
                                type="button"
                                className="spa-card__pick"
                                aria-pressed={on}
                                aria-label={`${service.name}, ${service.price}, ${service.minutes} minutes. ${on ? 'Selected' : 'Select'}`}
                                onClick={() => setChosen(on ? null : service.name)}
                            >
                                <img src={service.imageUrl} alt="" className="service-image" loading="lazy" />
                                <span className="service-details">
                                    <h4>{service.name} · {service.price}</h4>
                                    <p>{service.description}</p>
                                </span>
                            </button>
                            <div className="spa-card__foot">
                                <Chip tone={on ? 'info' : 'muted'}>{on ? 'Selected' : `${service.minutes} min`}</Chip>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => { setChosen(service.name); sheet.open({ service }); }}
                                >
                                    Book
                                </button>
                            </div>
                        </Rise>
                    );
                })}
            </div>

            <BookingSheet
                item={sheet.item}
                onClose={sheet.close}
                date={checked || selectedDate}
                slot={slot}
                slotsFor={slotsFor}
                onBook={book}
            />
        </div>
    );
}

/* The last step of a booking: the day and time (carried over from the
   availability check when there was one), a therapist, and a tick. */
function BookingSheet({ item, onClose, date, slot, slotsFor, onBook }) {
    const shown = useLast(item);
    const [openFor, setOpenFor] = useState(null);
    const [day, setDay] = useState(iso(0));
    const [time, setTime] = useState('');
    const [therapist, setTherapist] = useState(THERAPISTS[0]);
    const [note, setNote] = useState(null);
    const [booked, setBooked] = useState(null);

    /* Each opening starts from what the reader already chose above. */
    if (item !== openFor) {
        setOpenFor(item);
        if (item) {
            const start = date || iso(1);
            setDay(start);
            setTime(slot && !slotsFor(start).find(s => s.time === slot)?.taken ? slot : '');
            setTherapist(THERAPISTS[0]);
            setNote(null);
            setBooked(null);
        }
    }

    if (!shown) return <Modal open={false} onClose={onClose} />;
    const service = shown.service;

    const confirm = () => {
        if (!time) return setNote({ text: 'Choose a time first.', tone: 'warn', key: Date.now() });
        const details = { service: service.name, minutes: service.minutes, date: day, time, therapist };
        onBook(details);
        setBooked(details);
    };

    const actions = booked
        ? <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        : (
            <>
                <button type="button" className="btn btn-outline-light" onClick={onClose}>Not now</button>
                <button type="button" className="btn btn-primary" onClick={confirm}>Confirm booking · {service.price}</button>
            </>
        );

    return (
        <Modal open={Boolean(item)} onClose={onClose} eyebrow="Spa booking" title={service.name} actions={actions}>
            {booked ? (
                <Done title="You are booked">
                    <p>
                        {service.name}, {service.minutes} minutes, on {printDate(booked.date)} at {booked.time}
                        {booked.therapist !== THERAPISTS[0] ? ` with a ${booked.therapist.toLowerCase()}` : ''}.
                        Arrive fifteen minutes early to enjoy the steam room. {money(cost(service))} to your unit account.
                    </p>
                </Done>
            ) : (
                <>
                    <img className="modal__media" src={service.imageUrl} alt={service.name} />
                    <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                        <Chip tone="info">{service.minutes} minutes</Chip>
                        <Chip tone="muted">{service.price}</Chip>
                    </div>
                    <p>{service.description}. Robes, slippers and herbal tea are waiting in the lounge.</p>

                    <label className="field">
                        <span className="form-label">Date</span>
                        <input type="date" className="form-control" value={day} min={iso(0)}
                            onChange={e => { setDay(e.target.value || iso(0)); setTime(''); }} />
                    </label>

                    <div className="stack" style={{ gap: '0.5rem' }}>
                        <span className="form-label">Time</span>
                        {day ? (
                            <Slots slots={slotsFor(day)} value={time} onChange={t => { setTime(t); setNote(null); }} label="Appointment time" />
                        ) : <Empty>Pick a date to see open times.</Empty>}
                    </div>

                    <div className="stack" style={{ gap: '0.5rem' }}>
                        <span className="form-label">Therapist</span>
                        <Segments label="Therapist" options={THERAPISTS} value={therapist} onChange={setTherapist} />
                    </div>

                    <Note note={note} />
                </>
            )}
        </Modal>
    );
}

export default SpaScreen;
